import Duck from 'extensible-duck';
import Immutable from 'immutable';
import { push } from 'connected-react-router';
import moment from 'moment';
import { createSelector } from 'reselect';
import { toast } from 'react-toastify';
import { LOCATION_CHANGE } from 'connected-react-router';
import requestsManagerAxios from '../../lib/requestsManagerAxios';
import handleServerError2 from '../../lib/functions/handleServerError2';
import queryString from 'query-string5';
import memoize from 'fast-memoize';
import React from 'react';

export const dateNode = memoize((event, useBold) => {
  const momentTodayDateString = moment().format('YYYY-MM-DD');
  const momentTodayUTC = moment.utc(momentTodayDateString);
  const startMomentObject = moment.utc(event.start_date_time);
  const isThisYear =
    startMomentObject.format('YYYY') === momentTodayUTC.year().toString();
  const formattedStartTime =
    startMomentObject.minutes() === 0 ? 'h a' : 'h:mm a';
  if (event.all_day && event.show_end_date_time) {
    // show date to date
    const endMomentObject = moment.utc(event.end_date_time);
    return (
      <span>
        <span style={{ fontWeight: useBold ? 'bold' : 'normal' }}>
          {startMomentObject.format(`MMM D${isThisYear ? '' : ', YYYY'} (ddd)`)}
        </span>
        <span> to </span>
        <span style={{ fontWeight: useBold ? 'bold' : 'normal' }}>
          {endMomentObject.format(`MMM D${isThisYear ? '' : ', YYYY'} (ddd)`)}
        </span>
      </span>
    );
  } else if (event.all_day && !event.show_end_date_time) {
    // show start date only
    return (
      <span>
        <span style={{ fontWeight: useBold ? 'bold' : 'normal' }}>
          {startMomentObject.format(`MMM D${isThisYear ? '' : ', YYYY'} (ddd)`)}
        </span>
      </span>
    );
  } else if (!event.all_day && !event.show_end_date_time) {
    // show start datetime only
    return (
      <span>
        <span style={{ fontWeight: useBold ? 'bold' : 'normal' }}>
          {startMomentObject.format(`MMM D${isThisYear ? '' : ', YYYY'} (ddd)`)}
        </span>
        <span> / {startMomentObject.format(formattedStartTime)}</span>
      </span>
    );
  } else if (!event.all_day && event.show_end_date_time) {
    // show start datetime to end datetime
    const endMomentObject = moment.utc(event.end_date_time);
    const formattedEndTime = endMomentObject.minutes() === 0 ? 'h a' : 'h:mm a';

    if (
      startMomentObject.format('MM/DD/YYYY') ===
      endMomentObject.format('MM/DD/YYYY')
    ) {
      return (
        <span>
          <span style={{ fontWeight: useBold ? 'bold' : 'normal' }}>
            {startMomentObject.format(
              `MMM D${isThisYear ? '' : ', YYYY'} (ddd)`
            )}
          </span>
          <span> / {startMomentObject.format(formattedStartTime)}</span>
          <span> - {endMomentObject.format(formattedEndTime)}</span>
        </span>
      );
    } else {
      return (
        <span>
          <span style={{ fontWeight: useBold ? 'bold' : 'normal' }}>
            {startMomentObject.format(
              `MMM D${isThisYear ? '' : ', YYYY'} (ddd)`
            )}
          </span>
          <span> / {startMomentObject.format(formattedStartTime)}</span>
          <span> to </span>
          <span style={{ fontWeight: useBold ? 'bold' : 'normal' }}>
            {endMomentObject.format(`MMM D${isThisYear ? '' : ', YYYY'} (ddd)`)}
          </span>
          <span> / {endMomentObject.format(formattedEndTime)}</span>
        </span>
      );
    }
  }
});

export default new Duck({
  namespace: 'public',
  store: 'schedule',
  types: [
    'CREATE_EVENT',
    'CREATE_EVENT_PENDING',
    'CREATE_EVENT_REJECTED',
    'CREATE_EVENT_FULFILLED',
    'CREATE_SCHEDULE',
    'CREATE_SCHEDULE_PENDING',
    'CREATE_SCHEDULE_REJECTED',
    'CREATE_SCHEDULE_FULFILLED',
    'DELETE_EVENT',
    'DELETE_EVENT_PENDING',
    'DELETE_EVENT_REJECTED',
    'DELETE_EVENT_FULFILLED',
    'DELETE_SCHEDULE',
    'DELETE_SCHEDULE_PENDING',
    'DELETE_SCHEDULE_REJECTED',
    'DELETE_SCHEDULE_FULFILLED',
    'FETCH_EVENTS',
    'FETCH_EVENTS_PENDING',
    'FETCH_EVENTS_REJECTED',
    'FETCH_EVENTS_FULFILLED',
    'UPDATE_EVENT',
    'UPDATE_EVENT_PENDING',
    'UPDATE_EVENT_REJECTED',
    'UPDATE_EVENT_FULFILLED',
    'UPDATE_SCHEDULE',
    'UPDATE_SCHEDULE_PENDING',
    'UPDATE_SCHEDULE_REJECTED',
    'UPDATE_SCHEDULE_FULFILLED',
  ],
  initialState: Immutable.fromJS({
    $$events: [],
    $$schedules: [],
    eventsCurrentLoadedPage: 0,
    eventsIsLastPage: false,
    isFetching: true,
    isPending: false,
    selectedScheduleId: -1,
  }),
  reducer: ($$state, { type, payload, meta }, { types }) => {
    switch (type) {
      case types.CREATE_EVENT_PENDING:
        return $$state.merge({ isPending: true });
      case types.CREATE_EVENT_REJECTED:
        handleServerError2(payload);
        return $$state.merge({ isPending: false });
      case types.CREATE_EVENT_FULFILLED:
        return $$state
          .merge({ isPending: false })
          .update('$$events', $$events =>
            $$events.push(Immutable.fromJS(payload.data))
          );
      case types.CREATE_SCHEDULE_PENDING:
        return $$state.merge({ isPending: true });
      case types.CREATE_SCHEDULE_REJECTED:
        handleServerError2(payload);
        return $$state.merge({ isPending: false });
      case types.CREATE_SCHEDULE_FULFILLED:
        return $$state
          .merge({ isPending: false })
          .update('$$schedules', $$schedules =>
            $$schedules.push(Immutable.fromJS(payload.data.schedule))
          );
      case types.DELETE_EVENT_PENDING:
        return $$state.merge({ isPending: true });
      case types.DELETE_EVENT_REJECTED:
        handleServerError2(payload);
        return $$state.merge({ isPending: false });
      case types.DELETE_EVENT_FULFILLED:
        toast.success('Event deleted');
        return $$state
          .merge({ isPending: false })
          .update('$$events', $$events =>
            $$events.filterNot($$event => $$event.get('id') === meta.eventId)
          );
      case types.DELETE_SCHEDULE_PENDING:
        return $$state.merge({ isPending: true });
      case types.DELETE_SCHEDULE_REJECTED:
        handleServerError2(payload);
        return $$state.merge({ isPending: false });
      case types.DELETE_SCHEDULE_FULFILLED:
        return $$state
          .merge({
            isPending: false,
            $$events: Immutable.List(),
            selectedScheduleId: -1,
          })
          .update('$$schedules', $$schedules =>
            $$schedules.filterNot(
              $$schedule => $$schedule.get('id') === meta.scheduleId
            )
          );
      case types.FETCH_EVENTS_PENDING:
        return $$state.merge({ isFetching: true });
      case types.FETCH_EVENTS_REJECTED:
        handleServerError2(payload);
        return $$state.merge({ isFetching: false });
      case types.FETCH_EVENTS_FULFILLED:
        return $$state
          .merge({
            isFetching: false,
            eventsCurrentLoadedPage: payload.data.currentPage,
            eventsIsLastPage: payload.data.isLastPage,
          })
          .update('$$events', $$events =>
            $$events.concat(Immutable.fromJS(payload.data.events))
          );
      case LOCATION_CHANGE: {
        const { pathname, search } = payload.location;
        if (pathname !== '/events') return $$state;
        const params = queryString.parse(search);
        return $$state.merge({
          $$events: Immutable.List(),
          eventsCurrentLoadedPage: 0,
          eventFilter: params.filter === undefined ? 'recent' : params.filter,
          eventsIsLastPage: false,
          selectedScheduleId:
            params.schedule_id === undefined ? -1 : Number(params.schedule_id),
        });
      }
      case types.UPDATE_EVENT_PENDING:
        return $$state.merge({ isPending: true });
      case types.UPDATE_EVENT_REJECTED:
        handleServerError2(payload);
        return $$state.merge({ isPending: false });
      case types.UPDATE_EVENT_FULFILLED: {
        // if selectedScheduleId is not -1 and payload.data.schedule_id is not selectedScheduleId, then remove it, else update it
        const $$returnState = $$state.merge({ isPending: false });
        return [-1, payload.data.schedule_id].includes(
          $$state.get('selectedScheduleId')
        )
          ? $$returnState.update('$$events', $$events =>
              $$events.map($$event =>
                $$event.get('id') === payload.data.id
                  ? Immutable.fromJS(payload.data)
                  : $$event
              )
            )
          : $$returnState.update('$$events', $$events =>
              $$events.filterNot(
                $$event => $$event.get('id') === payload.data.id
              )
            );
      }
      case types.UPDATE_SCHEDULE_PENDING:
        return $$state.merge({ isPending: true });
      case types.UPDATE_SCHEDULE_REJECTED:
        handleServerError2(payload);
        return $$state.merge({ isPending: false });
      case types.UPDATE_SCHEDULE_FULFILLED:
        return $$state
          .merge({ isPending: false })
          .update('$$schedules', $$schedules =>
            $$schedules.map($$schedule =>
              $$schedule.get('id') === payload.data.schedule.id
                ? Immutable.fromJS(payload.data.schedule)
                : $$schedule
            )
          );
      default:
        return $$state;
    }
  },
  selectors: {
    $$events: new Duck.Selector(_selectors =>
      createSelector(
        $$state => $$state.get('$$events'),
        $$state => $$state.get('$$schedules'),
        ($$events, $$schedules) =>
          $$events
            .map($$event =>
              $$event.merge({
                schedule: $$schedules.find(
                  $$schedule =>
                    $$schedule.get('id') === $$event.get('schedule_id')
                ),
              })
            )
            .sortBy($$event => moment($$event.get('start_date_time')).unix())
      )
    ),
    $$schedules: $$state => {
      const $$schedules = $$state.get('$$schedules');
      return $$schedules.sortBy($$schedule =>
        $$schedule.get('title').toLowerCase()
      );
    },
    linkTo: $$scheduleStore => object => {
      const schedule_id = $$scheduleStore.get('selectedScheduleId');
      const filter = $$scheduleStore.get('eventFilter');
      const paramsObject = {
        schedule_id,
        filter,
        ...object,
      };

      let params = queryString.stringify({
        schedule_id:
          paramsObject.schedule_id === -1
            ? undefined
            : paramsObject.schedule_id,
        filter:
          paramsObject.filter === 'recent' ? undefined : paramsObject.filter,
      });
      params = params ? `?${params}` : '';
      return `/events${params}`;
    },
    selectedScheduleName: $$state => {
      const $$schedules = $$state.get('$$schedules');
      const selectedScheduleId = $$state.get('selectedScheduleId');
      if (selectedScheduleId === -1) return 'All Upcoming Events';
      return $$schedules
        .find($$schedule => $$schedule.get('id') === selectedScheduleId)
        .get('title');
    },
  },
  creators: ({ types }) => ({
    createEvent: (event, scheduleId) => ({
      type: types.CREATE_EVENT,
      payload: requestsManagerAxios.post(
        `/admin/schedules/${scheduleId}/events`,
        { event }
      ),
    }),
    createSchedule: schedule => ({
      type: types.CREATE_SCHEDULE,
      payload: requestsManagerAxios.post('/admin/schedules', {
        schedule,
      }),
    }),
    deleteEvent: eventId => ({
      type: types.DELETE_EVENT,
      payload: requestsManagerAxios.delete(`/admin/events/${eventId}`),
      meta: { eventId },
    }),
    deleteSchedule: scheduleId => (dispatch, _getState) => {
      dispatch({
        type: types.DELETE_SCHEDULE,
        payload: requestsManagerAxios.delete(`/admin/schedules/${scheduleId}`),
        meta: { scheduleId },
      }).then(() => dispatch(push('/events')));
    },
    fetchEvents: () => (dispatch, getState) => {
      const { $$scheduleStore } = getState();
      const params = queryString.stringify({
        schedule_id: $$scheduleStore.get('selectedScheduleId'),
        filter: $$scheduleStore.get('eventFilter'),
        page: $$scheduleStore.get('eventsCurrentLoadedPage') + 1,
      });
      return dispatch({
        type: types.FETCH_EVENTS,
        payload: requestsManagerAxios.get(`/events.json?${params}`),
      });
    },
    updateEvent: (event, result_only = false) => ({
      type: types.UPDATE_EVENT,
      payload: requestsManagerAxios.patch(`/admin/events/${event.id}`, {
        event,
        result_only,
      }),
    }),
    updateSchedule: (scheduleId, schedule) => ({
      type: types.UPDATE_SCHEDULE,
      payload: requestsManagerAxios.patch(`/admin/schedules/${scheduleId}`, {
        schedule,
      }),
    }),
  }),
});
